/**
 * Procedural prop geometry.
 *
 * Nothing here loads. ToyRaiders has to open in a couple of seconds on a phone
 * over a CrazyGames iframe, so the whole art set is boxes, cylinders and cones
 * assembled at boot — a few kilobytes of code instead of a few megabytes of
 * GLB. It also means a new prop is a function, not an asset pipeline.
 *
 * Each prop yields up to two merged geometries — `body` and `accent` — so one
 * prop type costs at most two instanced draw calls no matter how many are
 * placed.
 */

import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'

import type { PropTypeId } from '../core/props'
import { facet } from './toon'

export interface PropGeometry {
  body: THREE.BufferGeometry
  accent: THREE.BufferGeometry | null
}

function at(g: THREE.BufferGeometry, x: number, y: number, z: number): THREE.BufferGeometry {
  g.translate(x, y, z)
  return g
}

function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const merged = parts.length === 1 ? parts[0]! : mergeGeometries(parts)!
  if (parts.length > 1) for (const p of parts) p.dispose()
  return facet(merged)
}

const box = (w: number, h: number, d: number) => new THREE.BoxGeometry(w, h, d)
const cyl = (r: number, h: number, seg = 8) => new THREE.CylinderGeometry(r, r, h, seg)
const cone = (r: number, h: number, seg = 6) => new THREE.ConeGeometry(r, h, seg)
const ball = (r: number) => new THREE.SphereGeometry(r, 8, 6)

function buildProp(id: PropTypeId): PropGeometry {
  switch (id) {
    case 'brick': {
      const studs: THREE.BufferGeometry[] = []
      for (const sx of [-0.24, 0.24]) {
        for (const sz of [-0.24, 0.24]) studs.push(at(cyl(0.13, 0.1), sx, 1.15, sz))
      }
      return { body: merge([at(box(0.96, 1.1, 0.96), 0, 0.55, 0)]), accent: merge(studs) }
    }
    case 'bluff': {
      // Reads exactly as dangerous as a real trap. That is its entire function.
      const spikes: THREE.BufferGeometry[] = []
      for (const sx of [-0.26, 0, 0.26]) {
        for (const sz of [-0.26, 0.26]) spikes.push(at(cone(0.09, 0.2), sx, 0.16, sz))
      }
      return { body: merge([at(box(0.9, 0.08, 0.9), 0, 0.04, 0)]), accent: merge(spikes) }
    }
    case 'spring': {
      // An arrow you can read from across the room: it tells the raider where
      // they are about to go, and they still walk into it.
      const shaft = at(box(0.14, 0.06, 0.44), 0, 0.15, -0.06)
      const head = at(cone(0.19, 0.26, 4).rotateX(Math.PI / 2), 0, 0.15, 0.29)
      const coil = at(cyl(0.3, 0.1, 10), 0, 0.06, 0)
      return { body: merge([at(box(0.9, 0.1, 0.9), 0, 0.05, 0), coil]), accent: merge([shaft, head]) }
    }
    case 'swatter': {
      // Post only — the arm is animated separately, see roomView.
      return {
        body: merge([at(cyl(0.16, 1.0), 0, 0.5, 0), at(box(0.44, 0.1, 0.44), 0, 0.05, 0)]),
        accent: merge([at(cyl(0.2, 0.14, 10), 0, 1.02, 0)]),
      }
    }
    case 'marbles': {
      const marbles: THREE.BufferGeometry[] = []
      const spots = [
        [-0.3, -0.28],
        [0.02, -0.34],
        [0.31, -0.1],
        [-0.22, 0.12],
        [0.12, 0.24],
        [-0.05, -0.05],
        [0.34, 0.32],
        [-0.34, 0.34],
      ] as const
      for (const [sx, sz] of spots) marbles.push(at(ball(0.11), sx, 0.11, sz))
      return { body: merge([at(box(0.92, 0.06, 0.92), 0, 0.03, 0)]), accent: merge(marbles) }
    }
    case 'glue': {
      const blobs: THREE.BufferGeometry[] = []
      for (const [sx, sz, r] of [
        [-0.18, -0.14, 0.2],
        [0.2, 0.06, 0.24],
        [-0.02, 0.26, 0.16],
      ] as const) {
        blobs.push(at(ball(r).scale(1, 0.34, 1), sx, 0.06, sz))
      }
      return { body: merge([at(box(0.9, 0.05, 0.9), 0, 0.025, 0)]), accent: merge(blobs) }
    }
    case 'fan': {
      // Housing only — the blades spin, see roomView.
      return {
        body: merge([
          at(box(0.78, 0.78, 0.22), 0, 0.52, -0.14),
          at(box(0.34, 0.16, 0.34), 0, 0.08, -0.1),
        ]),
        accent: merge([at(cyl(0.36, 0.08, 12).rotateX(Math.PI / 2), 0, 0.52, 0.02)]),
      }
    }
    case 'pit': {
      // The floor genuinely has no quad here; this is the shaft you see down.
      const walls: THREE.BufferGeometry[] = [
        at(box(0.96, 0.9, 0.04), 0, -0.45, -0.48),
        at(box(0.96, 0.9, 0.04), 0, -0.45, 0.48),
        at(box(0.04, 0.9, 0.96), -0.48, -0.45, 0),
        at(box(0.04, 0.9, 0.96), 0.48, -0.45, 0),
        at(box(0.96, 0.04, 0.96), 0, -0.9, 0),
      ]
      const rim: THREE.BufferGeometry[] = [
        at(box(1.0, 0.05, 0.08), 0, 0.02, -0.48),
        at(box(1.0, 0.05, 0.08), 0, 0.02, 0.48),
        at(box(0.08, 0.05, 1.0), -0.48, 0.02, 0),
        at(box(0.08, 0.05, 1.0), 0.48, 0.02, 0),
      ]
      return { body: merge(walls), accent: merge(rim) }
    }
    case 'decoy': {
      return {
        body: merge([at(box(0.62, 0.34, 0.46), 0, 0.17, 0)]),
        accent: merge([
          at(box(0.66, 0.12, 0.5), 0, 0.4, 0),
          at(box(0.1, 0.14, 0.52), 0, 0.3, 0),
        ]),
      }
    }
  }
}

const geometryCache = new Map<PropTypeId, PropGeometry>()

export function propGeometry(id: PropTypeId): PropGeometry {
  let hit = geometryCache.get(id)
  if (!hit) {
    hit = buildProp(id)
    geometryCache.set(id, hit)
  }
  return hit
}

/** The real chest. Bigger and shinier than a decoy — but only slightly. */
export function chestGeometry(): { body: THREE.BufferGeometry; accent: THREE.BufferGeometry } {
  return {
    body: merge([at(box(0.72, 0.42, 0.54), 0, 0.21, 0)]),
    accent: merge([
      at(box(0.78, 0.16, 0.6), 0, 0.5, 0),
      at(box(0.12, 0.18, 0.64), 0, 0.36, 0),
      at(cyl(0.07, 0.1, 8), 0, 0.28, 0.3),
    ]),
  }
}

/** Animated swatter arm: a paddle offset from its pivot. */
export function swatterArmGeometry(): THREE.BufferGeometry {
  return merge([at(box(0.1, 0.09, 0.66), 0, 0, 0.33), at(box(0.5, 0.08, 0.36), 0, 0, 0.78)])
}

/** Animated fan blades. */
export function fanBladeGeometry(): THREE.BufferGeometry {
  const blades: THREE.BufferGeometry[] = []
  for (let i = 0; i < 4; i++) {
    const b = box(0.5, 0.02, 0.16)
    b.rotateZ((i * Math.PI) / 2)
    blades.push(b)
  }
  return merge(blades)
}

export function disposePropGeometries(): void {
  for (const g of geometryCache.values()) {
    g.body.dispose()
    g.accent?.dispose()
  }
  geometryCache.clear()
}
